import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiJson, ApiError } from "../../../lib/api";

import { getTenantUuid } from "../../ingress/tenant"; // adjust path if needed

const TENANT_UUID = getTenantUuid();

type ReqRow = {
  id: string;
  status: string;
  run_id?: string | null;
  error?: string | null;
  app_id?: string;
  tenant_label?: string;
  created_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
};

function normalizeList(payload: any): ReqRow[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.requests)) return payload.requests;
  }
  return [];
}

function fmtDt(s?: string | null) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return String(s);
  }
}

function useQuery() {
  const loc = useLocation();
  return useMemo(() => new URLSearchParams(loc.search), [loc.search]);
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function AdminIngressRequestsPage() {
  const nav = useNavigate();
  const query = useQuery();

  const [rows, setRows] = useState<ReqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // filters
  const [status, setStatus] = useState<string>("");
  const [appId, setAppId] = useState<string>("");

  // side panel for quick output
  const [selected, setSelected] = useState<ReqRow | null>(null);
  const [outputLoading, setOutputLoading] = useState(false);
  const [outputErr, setOutputErr] = useState<string | null>(null);
  const [outputJson, setOutputJson] = useState<any>(null);

  // initialize app_id filter from query param (and react to changes)
  useEffect(() => {
    const qApp = query.get("app_id") || "";
    // only overwrite if query explicitly provides it
    if (qApp !== "" && qApp !== appId) setAppId(qApp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams();
      qs.set("tenant_id", TENANT_UUID);
      if (status) qs.set("status", status);
      if (appId) qs.set("app_id", appId);
      qs.set("limit", "200");

      const data = await apiJson<any>(`/v1/admin/ingress/requests?${qs.toString()}`);
      const list = normalizeList(data);
      setRows(list);

      // keep selection in sync if current selection disappears
      if (selected) {
        const stillThere = list.find((r) => r.id === selected.id);
        if (!stillThere) {
          setSelected(null);
          setOutputJson(null);
          setOutputErr(null);
        } else {
          setSelected(stillThere);
        }
      }
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, appId]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  function statusBadge(st: string) {
    const base = "inline-flex items-center px-2 py-1 rounded border text-xs";
    if (st === "succeeded") return <span className={`${base} border-green-300 text-green-700 bg-green-50`}>succeeded</span>;
    if (st === "failed") return <span className={`${base} border-red-300 text-red-700 bg-red-50`}>failed</span>;
    if (st === "running") return <span className={`${base} border-blue-300 text-blue-700 bg-blue-50`}>running</span>;
    if (st === "queued") return <span className={`${base} border-gray-300 text-gray-700 bg-gray-50`}>queued</span>;
    return <span className={`${base} border-gray-300 text-gray-700 bg-gray-50`}>{st}</span>;
  }

  async function openQuickOutput(r: ReqRow) {
    setSelected(r);
    setOutputErr(null);
    setOutputJson(null);

    setOutputLoading(true);
    try {
      // NOTE: allow fetching even when failed/running; backend returns 404 if truly absent
      const out = await apiJson<any>(`/v1/admin/ingress/requests/${r.id}/output?tenant_id=${TENANT_UUID}`);
      setOutputJson(out);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : String(e);
      // give a nicer message for the common case
      if (msg.includes("output not ready") || msg.includes("404")) {
        setOutputErr("Output not ready yet (or not persisted). Try again in a moment.");
      } else {
        setOutputErr(msg);
      }
    } finally {
      setOutputLoading(false);
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Ingress Requests</h2>
          <div className="text-sm text-gray-600 mt-1">
            {Object.keys(counts).length
              ? Object.entries(counts).map(([k, v]) => `${k}:${v}`).join(" · ")
              : "—"}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">app_id</span>
            <input
              className="border rounded px-3 py-2 text-sm w-[320px] max-w-full font-mono"
              placeholder="(optional) filter by app_id"
              value={appId}
              onChange={(e) => setAppId(e.target.value.trim())}
            />
            {appId && (
              <button
                className="px-2 py-2 rounded border text-xs hover:bg-gray-50"
                onClick={() => setAppId("")}
                title="Clear app_id filter"
              >
                ✕
              </button>
            )}
          </div>

          <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="queued">queued</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
          </select>

          <button className="px-3 py-2 rounded border text-sm hover:bg-gray-50" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-4 text-sm text-red-600 whitespace-pre-wrap">
          {err}
          <div className="mt-2 text-xs text-gray-600">
            This page uses <code>GET /v1/admin/ingress/requests</code>. If you see 404, confirm <code>backend/app/routers/admin/ingress.py</code> is mounted in <code>app/main.py</code>.
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Table */}
        <div className="lg:col-span-2 border rounded bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
            <div className="col-span-5">Request</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3">Run</div>
            <div className="col-span-2">Created</div>
          </div>

          {loading ? (
            <div className="p-4 text-sm text-gray-600">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-sm text-gray-600">No requests found.</div>
          ) : (
            rows.map((r) => {
              const isSelected = selected?.id === r.id;
              return (
                <div
                  key={r.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 border-t text-sm cursor-pointer ${
                    isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  onClick={() => nav(`/app/admin/ingress/requests/${r.id}`)}
                  title="Click to open request detail"
                >
                  <div className="col-span-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs text-gray-800">{r.id}</code>
                      <button
                        className="px-2 py-1 rounded border text-[11px] hover:bg-white"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await copyToClipboard(r.id);
                        }}
                        title="Copy request_id"
                      >
                        Copy
                      </button>
                      <button
                        className="px-2 py-1 rounded border text-[11px] hover:bg-white"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await openQuickOutput(r);
                        }}
                        title="Quick output"
                      >
                        Output
                      </button>
                    </div>

                    {r.error && <div className="text-xs text-red-600 mt-1 line-clamp-2">{r.error}</div>}

                    {r.app_id && (
                      <div className="text-[11px] text-gray-500 mt-1">
                        app_id: <code>{r.app_id}</code>
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">{statusBadge(r.status)}</div>

                  <div className="col-span-3 text-xs text-gray-700">
                    {r.run_id ? <code>{r.run_id}</code> : <span className="text-gray-400">—</span>}
                  </div>

                  <div className="col-span-2 text-xs text-gray-700">{fmtDt(r.created_at)}</div>
                </div>
              );
            })
          )}
        </div>

        {/* Side panel */}
        <div className="border rounded bg-white p-4">
          <div className="font-medium">Quick View</div>
          <div className="text-xs text-gray-500 mt-1">
            Click “Output” on a row to fetch <code>/v1/admin/ingress/requests/:id/output</code>
          </div>

          {!selected ? (
            <div className="mt-4 text-sm text-gray-600">Select a request to view output.</div>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="text-xs text-gray-500">request_id</div>
              <div className="font-mono text-xs break-all">{selected.id}</div>

              <div className="text-xs text-gray-500 mt-2">status</div>
              <div>{statusBadge(selected.status)}</div>

              <div className="text-xs text-gray-500 mt-2">run_id</div>
              <div className="font-mono text-xs break-all">{selected.run_id || "—"}</div>

              <div className="text-xs text-gray-500 mt-2">actions</div>
              <div className="flex gap-2 flex-wrap">
                <Link
                  className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                  to={`/app/admin/ingress/requests/${selected.id}`}
                >
                  Open Detail
                </Link>
                <button
                  className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                  onClick={() => openQuickOutput(selected)}
                  disabled={outputLoading}
                >
                  Refresh Output
                </button>
              </div>

              {outputLoading && <div className="mt-3 text-sm text-gray-600">Loading output…</div>}
              {outputErr && <div className="mt-3 text-sm text-red-600 whitespace-pre-wrap">{outputErr}</div>}

              {outputJson && (
                <pre className="mt-3 text-[11px] bg-gray-50 border rounded p-2 overflow-auto max-h-[420px]">
                  {JSON.stringify(outputJson, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>

      {/* footer hint */}
      <div className="mt-4 text-xs text-gray-500">
        Tip: From an app detail page, use <code>Requests</code> tab → “Open Full Monitor” which deep-links with <code>?app_id=...</code>.
      </div>
    </div>
  );
}